/**
 * Mess Attendance — per-meal warden check-in + summary + anomalies.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const MEALS = ["breakfast", "lunch", "snacks", "dinner"];
const today = () => new Date().toISOString().slice(0, 10);

function MarkTab() {
  const [students, setStudents] = useState([]);
  const [meal, setMeal] = useState("breakfast");
  const [date, setDate] = useState(today());
  const [marks, setMarks] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/students").then((r) => {
      const hostelStudents = (r.data || []).filter((s) => s.hostel_id);
      setStudents(hostelStudents);
    });
  }, []);

  useEffect(() => {
    const m = {};
    students.forEach((s) => { m[s.id] = marks[s.id] || "present"; });
    setMarks(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [students.length]);

  const submit = async () => {
    setBusy(true);
    try {
      const rows = students.map((s) => ({ student_id: s.id, status: marks[s.id] || "present" }));
      await api.post("/mess/attendance/mark", { date, meal, rows });
      toast.success(`Marked ${rows.length} for ${meal} on ${date}`);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-3 items-end flex-wrap">
        <div>
          <Label>Meal</Label>
          <Select value={meal} onValueChange={setMeal}>
            <SelectTrigger data-testid="mess-att-meal" className="bg-white capitalize w-40"><SelectValue /></SelectTrigger>
            <SelectContent>{MEALS.map((m) => (<SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div><Label>Date</Label><Input data-testid="mess-att-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-white" /></div>
        <Button data-testid="mess-att-submit" onClick={submit} disabled={busy || students.length === 0}>{busy ? "Saving…" : `Mark ${students.length}`}</Button>
      </div>
      <div className="vs-card divide-y">
        {students.map((s) => (
          <div key={s.id} className="flex items-center gap-3 p-3" data-testid={`ma-row-${s.id}`}>
            <div className="flex-1">
              <div className="font-medium text-sm">{s.name}</div>
              <div className="text-xs text-[var(--vs-text-secondary)] font-mono">{s.vs_id} · {s.class_name || "—"}</div>
            </div>
            <div className="flex gap-1">
              {["present", "absent"].map((st) => (
                <button
                  key={st}
                  type="button"
                  data-testid={`ma-${s.id}-${st}`}
                  onClick={() => setMarks((m) => ({ ...m, [s.id]: st }))}
                  className={`text-xs px-2 py-1 capitalize rounded border transition ${
                    marks[s.id] === st ? (st === "present" ? "bg-emerald-600 border-emerald-600 text-white" : "bg-red-600 border-red-600 text-white") : "bg-white border-[var(--vs-border)]"
                  }`}
                >{st}</button>
              ))}
            </div>
          </div>
        ))}
        {!students.length && <div className="p-4 text-sm text-center text-[var(--vs-text-secondary)]">No hostel students in scope.</div>}
      </div>
    </div>
  );
}

function SummaryTab() {
  const [from, setFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); });
  const [to, setTo] = useState(today());
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { const r = await api.get("/mess/attendance/summary", { params: { date_from: from, date_to: to } }); setRows(r.data.rows || []); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="space-y-3">
      <div className="flex gap-3 items-end flex-wrap">
        <div><Label>From</Label><Input data-testid="ma-sum-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-white" /></div>
        <div><Label>To</Label><Input data-testid="ma-sum-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-white" /></div>
        <Button data-testid="ma-sum-load" onClick={load}>Load</Button>
      </div>
      <div className="vs-card divide-y">
        {rows.map((r) => (
          <div key={r.student_id} className="p-3 flex items-center gap-3" data-testid={`ma-sum-${r.student_id}`}>
            <div className="flex-1">
              <div className="font-medium text-sm">{r.name}</div>
              <div className="text-xs text-[var(--vs-text-secondary)] font-mono">{r.vs_id} · {r.class_name || "—"}</div>
            </div>
            <div className="text-sm font-mono flex gap-3">
              <span className="text-emerald-600">P:{r.present}</span>
              <span className="text-red-600">A:{r.absent}</span>
              <span className="font-bold w-14 text-right">{r.skip_pct != null ? `${r.skip_pct}%` : "—"}</span>
            </div>
          </div>
        ))}
        {!rows.length && <div className="p-4 text-sm text-center text-[var(--vs-text-secondary)]">No data.</div>}
      </div>
    </div>
  );
}

function AnomaliesTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/mess/attendance/anomalies").then((r) => setRows(r.data)); }, []);
  return (
    <div className="vs-card divide-y" data-testid="ma-anom">
      <div className="p-3 bg-amber-50 text-xs text-amber-900">Students who skipped <strong>3+ meals</strong> in the last 7 days (flag for warden follow-up).</div>
      {rows.map((r) => (
        <div key={r.student_id} className="p-3 flex items-center gap-3" data-testid={`ma-anom-${r.student_id}`}>
          <div className="flex-1">
            <div className="font-medium text-sm">{r.name}</div>
            <div className="text-xs text-[var(--vs-text-secondary)] font-mono">{r.vs_id} · {r.class_name || "—"} · {r.parent_phone || "—"}</div>
          </div>
          <span className="font-bold text-red-600 text-sm">{r.skipped} skipped</span>
        </div>
      ))}
      {!rows.length && <div className="p-4 text-sm text-center text-[var(--vs-text-secondary)]">No anomalies — everyone is eating well 🙏</div>}
    </div>
  );
}

export default function MessAttendance() {
  return (
    <DashboardLayout>
      <div className="vs-section">
        <h1 className="text-2xl font-semibold">Mess attendance</h1>
        <p className="text-sm text-[var(--vs-text-secondary)]">Per-meal warden check-in for hostel residents. Anomalies flag students who skip 3+ meals in 7 days.</p>
      </div>
      <Tabs defaultValue="mark">
        <TabsList>
          <TabsTrigger value="mark" data-testid="tab-ma-mark">Mark</TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-ma-summary">Summary</TabsTrigger>
          <TabsTrigger value="anom" data-testid="tab-ma-anom">Anomalies</TabsTrigger>
        </TabsList>
        <TabsContent value="mark"><MarkTab /></TabsContent>
        <TabsContent value="summary"><SummaryTab /></TabsContent>
        <TabsContent value="anom"><AnomaliesTab /></TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
